import { Infinity as SemLimite, Pause, Play, Plus, SkipForward, Square } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useTurnContextOptional } from '../contexts'
import type { TurnState } from '@/types/turn'

interface Permissions {
  canControlTurn: boolean
}

export interface ComandosDaRodadaProps {
  // Opcionais: sem elas, lê do contexto (como a BarraDoTurno e a FilaDoTurno)
  turnState?: TurnState | null
  permissions?: Permissions
  onAdjustTime?: (seconds: number) => void
  onNextPlayer?: () => void
  onEndTurn?: () => void
  onPauseTurn?: () => void
  onStartCountdown?: () => void
}

const CORES = {
  emerald: 'text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300 border-emerald-500/30',
  amber: 'text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 border-amber-500/30',
  violet: 'text-violet-400 hover:bg-violet-500/10 hover:text-violet-300 border-violet-500/30',
  rose: 'text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 border-rose-500/30',
} as const

/**
 * Os comandos da rodada, na altura do polegar (2026-07-27).
 *
 * Eles viviam na `BarraDoTurno`, no alto da tela — e o mestre conduz a aula
 * pelo celular, com uma mão. "Próximo" é o botão mais tocado da mesa inteira e
 * ficava no canto mais distante do polegar; alcançá-lo pedia trocar a pegada,
 * no meio de uma sala de aula. Agora a fileira mora entre o conteúdo e a caixa
 * de mensagem, e a barra do topo fica com o relógio, que é para ler e não para
 * tocar.
 *
 * **Com rótulo, sempre.** A `BarraDoTurno` escondia os rótulos abaixo de
 * `@[34rem]` porque disputava a barra com o relógio e o nome de quem joga;
 * aqui a linha é só dos quatro botões e a largura sobra. Ícone sozinho é
 * adivinhação — e "Encerrar" ao lado de "Próximo" é a adivinhação cara.
 *
 * No fluxo da coluna flex, como toda barra do projeto: nada de `position:
 * fixed` com padding compensatório, que foi o que escondia mensagens antes da
 * fase B.
 */
export function ComandosDaRodada(props: ComandosDaRodadaProps) {
  const contexto = useTurnContextOptional()

  const turnState = props.turnState !== undefined ? props.turnState : contexto?.turnState
  const permissions = props.permissions ?? contexto?.permissions
  const onAdjustTime = props.onAdjustTime ?? contexto?.onAdjustTime
  const onNextPlayer = props.onNextPlayer ?? contexto?.onNextPlayer
  const onEndTurn = props.onEndTurn ?? contexto?.onEndTurn
  const onPauseTurn = props.onPauseTurn ?? contexto?.onPauseTurn
  const onStartCountdown = props.onStartCountdown ?? contexto?.onStartCountdown

  // Sem rodada não há o que comandar; quem não conduz não vê a fileira. Abrir
  // a rodada continua sendo da barra do topo — é ato de antes, não de durante.
  if (!turnState?.isActive || !permissions?.canControlTurn) return null

  const parado = turnState.isPaused
  const semLimite = turnState.totalTime === 0

  const controles = [
    // Somar tempo a quem não tem relógio não faz nada visível (mesma regra da
    // BarraDoTurno, e a razão de o botão sumir lá também).
    ...(semLimite
      ? []
      : [{
          chave: 'somar',
          rotulo: '+30s',
          titulo: 'Somar 30 segundos ao tempo desta vez',
          Icone: Plus,
          acao: () => onAdjustTime?.(30),
          cor: 'emerald' as const,
        }]),
    parado
      ? {
          chave: 'iniciar',
          rotulo: 'Iniciar',
          titulo: 'Comecar a contar o tempo desta vez',
          Icone: Play,
          acao: onStartCountdown,
          cor: 'emerald' as const,
        }
      : {
          chave: 'pausar',
          rotulo: 'Pausar',
          titulo: 'Pausar a contagem',
          Icone: Pause,
          acao: onPauseTurn,
          cor: 'amber' as const,
        },
    {
      chave: 'proximo',
      rotulo: 'Proximo',
      titulo: 'Passar a vez para o proximo da fila',
      Icone: SkipForward,
      acao: onNextPlayer,
      cor: 'violet' as const,
    },
    {
      chave: 'encerrar',
      rotulo: 'Encerrar',
      titulo: 'Encerrar a rodada',
      Icone: Square,
      acao: onEndTurn,
      cor: 'rose' as const,
    },
  ]

  return (
    <div
      aria-label="Comandos da rodada"
      className="flex items-center gap-1.5 px-2 py-1.5 border-t border-game-border-primary dark:border-white/[0.05] bg-game-secondary/50 dark:bg-zinc-900/50"
    >
      {semLimite && (
        <SemLimite
          className="h-4 w-4 flex-shrink-0 text-emerald-400"
          aria-label="Rodada sem limite de tempo"
        />
      )}
      {controles.map(({ chave, rotulo, titulo, Icone, acao, cor }) => (
        <Button
          key={chave}
          variant="ghost"
          size="sm"
          onClick={acao}
          title={titulo}
          aria-label={titulo}
          // flex-1: os quatro dividem a largura em partes iguais, e cada alvo
          // fica bem maior que os 40px de antes — é uma fileira só de botões.
          className={cn(
            'h-10 flex-1 min-w-0 px-1 text-xs rounded-lg border',
            CORES[cor]
          )}
        >
          <Icone className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{rotulo}</span>
        </Button>
      ))}
    </div>
  )
}
